import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReactComponent as Logo } from '../../../assets/images/popashot_logo.svg';
import ErrorMessage from '../../common/ErrorMessage';
import LoadingButton from '../../common/LoadingButton';
import apiClient from '../../../utils/apiClient';
import { useLoading } from '../../../contexts/LoadingContext';

const NewPasswordStep = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { startLoading, stopLoading } = useLoading();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const email = searchParams.get('email');
    const token = searchParams.get('token');

    useEffect(() => {
        if (!email || !token) {
            navigate('/login');
        }
    }, [email, token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        startLoading('resetPassword');
        try {
            const response = await apiClient.post('/api/users/auth/reset-password/', {
                email: email,
                token: token,
                password: formData.password
            });

            // Store tokens and user data
            const userData = response.data.user;
            userData.has_set_password = true; // Ensure has_set_password is set to true
            localStorage.setItem('access_token', response.data.tokens.access);
            localStorage.setItem('refresh_token', response.data.tokens.refresh);
            localStorage.setItem('user', JSON.stringify(userData));

            // Check if profile setup is needed
            if (!response.data.user.profile_setup_complete) {
                navigate('/signup', { 
                    state: { 
                        step: 'birthdate',
                        email: email
                    }
                });
            } else {
                navigate('/profile');
            }
        } catch (error) {
            setError(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            stopLoading('resetPassword');
        }
    };

    return (
        <div className="space-y-8 bg-transparent">
            <div className="flex items-center justify-center">
                <Logo className="w-48 h-auto" />
            </div>

            <div className="text-center">
                <h1>Choose a new password</h1>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                <div className="rounded-md shadow-sm -space-y-px">
                    <div>
                        <label htmlFor="email" className="sr-only">
                            Email Address
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            value={email || ''}
                            disabled
                        />
                    </div>
                    <div>
                        <label htmlFor="password" className="sr-only">
                            New Password
                        </label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            required
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            placeholder="New password"
                            autoComplete="new-password"
                            enterKeyHint="next"
                            aria-label="New password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        />
                    </div>
                    <div>
                        <label htmlFor="confirmPassword" className="sr-only">
                            Confirm New Password
                        </label>
                        <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            required
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            placeholder="Confirm new password"
                            autoComplete="new-password"
                            enterKeyHint="done"
                            aria-label="Confirm new password"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        />
                    </div>
                </div>

                <ErrorMessage error={error} />

                <LoadingButton
                    type="submit"
                    className="w-full btn-primary"
                    loadingKey="resetPassword"
                >
                    Change password
                </LoadingButton>
            </form>
        </div>
    );
};

export default NewPasswordStep;
